import { Box, Button, Stack } from '@mui/material'
import React from 'react'
import { useFormContext, useWatch } from 'react-hook-form'

import { StyledRouterLink } from 'components/common/StyledRouterLink'
import { type AuthenticationFormValues } from 'components/status-checks/authentication-form-utils'
import {
  isExternalIntegrationAuthenticated,
  isObservabilityToolOfType
} from 'components/status-checks/authentication-form-validation-utils'
import { ObservabilityToolAuthenticateAndSaveButton } from 'components/status-checks/common/ObservabilityToolAuthenticateAndSaveButton'
import { ObservabilityToolAuthenticationStatus } from 'components/status-checks/common/ObservabilityToolAuthenticationStatus'
import { ObservabilityToolAndDomainSelect } from 'components/status-checks/form/authentication/ObservabilityToolAndDomainSelect'
import { ObservabilityToolAuthenticationDetails } from 'components/status-checks/form/authentication/ObservabilityToolAuthenticationDetails'
import { type StatusCheckFormValues } from 'components/status-checks/status-checks-form-utils'
import { getStatusChecksUrls } from 'components/status-checks/status-checks-urls-utils'
import { useObservabilityToolAuthenticationContext } from 'context/status-checks/ObservabilityToolAuthenticationContext'
import {
  ObservabilityToolType,
  type TeamExternalIntegration,
  TeamExternalIntegrationAuthenticationStatus
} from 'types/api/status-checks/status-check'

type FormValues = StatusCheckFormValues | AuthenticationFormValues

const statusChecksUrls = getStatusChecksUrls()
const fieldsToWatch = ['externalIntegration', 'observabilityTool'] as const

export const ObservabilityToolAuthentication: React.FC<{
  externalIntegrations: TeamExternalIntegration[]
  onCancel?: () => void
  onNext?(): void
}> = ({ externalIntegrations, onCancel, onNext }) => {
  const {
    authenticationResponse,
    hasErrorAwsIAMRoleMetadata,
    isDrawerFlow,
    isEdit
  } = useObservabilityToolAuthenticationContext()

  // react-hook-form field values and derivations
  const { control, formState, setValue, trigger } = useFormContext<FormValues>()
  const { isSubmitting } = formState
  const [externalIntegration, selectedObservabilityTool] = useWatch({
    control,
    name: fieldsToWatch
  })

  const isSelected = Boolean(selectedObservabilityTool)
  const isAuthenticated =
    isExternalIntegrationAuthenticated(externalIntegration)

  const isAwsObservabilityTool = isObservabilityToolOfType(
    ObservabilityToolType.AWS,
    selectedObservabilityTool
  )

  /*
   * Display the authentication configuration when:
   * 1. In edit mode (to allow re-authentication)
   * 2. When AWS is selected (to allow the multiple-step authentication process)
   * 3. When the observability tool is not authenticated and an observability tool is selected (to allow initial authentication)
   */
  const isAuthConfigurationAvailable =
    isEdit || isAwsObservabilityTool || (!isAuthenticated && isSelected)

  const handleTestTeamExternalIntegrationError = () => {
    setValue(
      'externalIntegration.lastAuthenticationStatus',
      TeamExternalIntegrationAuthenticationStatus.UnAuthenticated
    )
  }

  const handleTestTeamExternalIntegrationSuccess = async () => {
    await trigger('observabilityToolNickname')
    setValue(
      'externalIntegration.lastAuthenticationStatus',
      TeamExternalIntegrationAuthenticationStatus.Authenticated
    )
  }

  return (
    <Box sx={(muiTheme) => ({ maxWidth: muiTheme.breakpoints.values.sm })}>
      {/*hide select in drawer flow since we want to the auth type to only one option there*/}
      {!isDrawerFlow && (
        <ObservabilityToolAndDomainSelect
          externalIntegrations={externalIntegrations}
          isEdit={isEdit}
          selectedObservabilityTool={selectedObservabilityTool}
        />
      )}
      {selectedObservabilityTool.name && (
        <Stack sx={{ mt: 2 }}>
          {isAuthConfigurationAvailable && (
            <ObservabilityToolAuthenticationDetails
              observabilityToolType={selectedObservabilityTool.type}
            />
          )}
          <Stack spacing={2} sx={{ mt: 4 }}>
            <ObservabilityToolAuthenticationStatus
              authenticationResponse={authenticationResponse}
            />
            <Stack direction="row" spacing={2}>
              {(!isAuthenticated || isEdit || isSubmitting) && (
                <ObservabilityToolAuthenticateAndSaveButton
                  externalIntegrations={externalIntegrations}
                  onTestError={handleTestTeamExternalIntegrationError}
                  onTestSuccess={handleTestTeamExternalIntegrationSuccess}
                />
              )}
              {isAuthenticated && !isEdit && (
                <Button
                  data-testid="observability-tool-authentication-next-button"
                  disabled={
                    isAwsObservabilityTool ? hasErrorAwsIAMRoleMetadata : false
                  }
                  variant="contained"
                  onClick={onNext}
                >
                  Next
                </Button>
              )}
              {isEdit &&
                (isDrawerFlow ? (
                  <Button
                    color="secondary"
                    sx={{ height: 'fit-content', width: 'fit-content' }}
                    variant="contained"
                    onClick={onCancel}
                  >
                    Cancel
                  </Button>
                ) : (
                  <Button
                    replace
                    color="secondary"
                    component={StyledRouterLink}
                    sx={{ height: 'fit-content', width: 'fit-content' }}
                    to={statusChecksUrls.authentication()}
                    variant="contained"
                  >
                    Cancel
                  </Button>
                ))}
            </Stack>
          </Stack>
        </Stack>
      )}
    </Box>
  )
}
